import { addHours } from "date-fns";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { PersistOptions } from "zustand/middleware";
import type {
  AggregateOptions,
  DateRange,
  NodeTableChartState,
} from "~/types/ui";

export const defaultAggregateOptions: AggregateOptions = {
  fillEmpty: true,
  fillWith: null,
  functions: ["last"],
  windowSize: "auto",
};

// Computed live rather than reusing a module-load-frozen constant
export function freshRange(): DateRange {
  return {
    from: addHours(new Date(), -24),
    to: new Date(),
  };
}

export function freshChartState(): NodeTableChartState {
  return {
    aggregateOptions: defaultAggregateOptions,
    range: freshRange(),
  };
}

export interface UiNodeState {
  tableApplicationFilter: string[] | null;
  tableNodeTypeFilter: string[] | null;
  tableActiveStatusFilter: string | null;
  tableSelectedRowIds: Record<string, boolean>;
  tableSearchValue: string | null;
  tableChartState: NodeTableChartState;

  setTableApplicationFilter: (filterValues: string[] | string | null) => void;
  setTableNodeTypeFilter: (filterValues: string[] | string | null) => void;
  setTableActiveStatusFilter: (filterValue: string[] | string | null) => void;
  setTableSelectedRowIds: (rowIds: Record<string, boolean>) => void;
  setTableSearchValue: (searchValue: string | null) => void;
  setTableChartState: (chartState: NodeTableChartState) => void;

  resetChartState: () => void;
  reset: () => void;
}

const useUiNodeState = create<UiNodeState>()(
  persist(
    (set, _) => ({
      tableActiveStatusFilter: null,
      tableApplicationFilter: null,
      tableNodeTypeFilter: null,
      tableSelectedRowIds: {},
      tableSearchValue: null,
      tableChartState: freshChartState(),

      setTableActiveStatusFilter: (filterValue: string[] | string | null) =>
        set(() => ({ tableActiveStatusFilter: filterValue as string })),
      setTableApplicationFilter: (filterValues: string[] | string | null) =>
        set(() => ({ tableApplicationFilter: filterValues as string[] })),
      setTableNodeTypeFilter: (filterValues: string[] | string | null) =>
        set(() => ({ tableNodeTypeFilter: filterValues as string[] })),
      setTableSelectedRowIds: (rowIds: Record<string, boolean>) =>
        set(() => ({ tableSelectedRowIds: rowIds })),
      setTableSearchValue: (searchValue: string | null) =>
        set(() => ({ tableSearchValue: searchValue })),
      setTableChartState: (chartState: NodeTableChartState) =>
        set(() => ({ tableChartState: chartState })),
      resetChartState: () =>
        set(() => ({ tableChartState: freshChartState() })),

      reset: () =>
        set(() => ({
          tableActiveStatusFilter: null,
          tableApplicationFilter: null,
          tableNodeTypeFilter: null,
          tableSelectedRowIds: {},
          tableSearchValue: null,
          tableChartState: freshChartState(),
        })),
    }),
    {
      name: "ui-node-state",
      onRehydrateStorage: () => (state) => {
        if (state?.tableChartState?.range?.from) {
          state.tableChartState.range.from = new Date(
            state.tableChartState.range.from,
          );
        }

        if (state?.tableChartState?.range?.to) {
          state.tableChartState.range.to = new Date(
            state.tableChartState.range.to,
          );
        }
      },
    } as PersistOptions<UiNodeState>,
  ),
);

export default useUiNodeState;
